<script setup>
import {computed, onMounted, ref} from "vue";
import _, {isEmpty} from "lodash";
import {usePiniaStore} from "../../store/pinia_store";
import articleNumber from "../../mixins/article-number";

const props = defineProps({
  eventBus: {
    type: Object,
    required: true,
  },
  item: {
    type: Object,
    required: true,
  },
  dentaidApiInstance: {
    type: Function,
    required: true,
  },
  cartEventBus: {
    type: Object,
    required: true,
  }
})

const piniaStore = usePiniaStore();
const language = ref(window.drupalSettings.ef2_custom?.language ?? 'nl');

const deleteProductText = Drupal.t("Delete product");
const discountLabelText = Drupal.t("Discount");
// Register strings for Drupal translation extractor
Drupal.t("remaining");
Drupal.formatPlural(0, "@count free package", "@count free packages");

const articleNumberLabel = computed(() => articleNumber.methods.getArticleNumberLabel(props.item));

const showCnk = computed(() => {
  if (isEmpty(props.item?.dwh_product?.cnk_code)) {
    return;
  }

  return '- CNK ' + props.item?.dwh_product?.cnk_code;
});

const displayedArticleNumber = computed(() => articleNumber.methods.getDisplayedArticleNumber(props.item));


const lineDiscount = computed(() => {
  if (props.item.total_unit_price == null || props.item.total_net_price == null) {
    return 0;
  }

  return props.item.total_unit_price - props.item.total_net_price;
});


const hasLineDiscount = computed(() => lineDiscount.value > 0);

const isFrench = computed(() => {
  return language.value === 'fr';
});

const lineDiscountPercentage = computed(() => props.item.discount_percentage ?? 0);
const magnumDiscountInfo = computed(() => {
  return piniaStore.magnumDiscountInfo(
    props.item.sku,
    props.item.quantity,
    props.item.available_quantity_prices,
    props.item.unit_price,
    lineDiscount.value
  );
});

const magnumDiscountText = computed(() => {
  return piniaStore.magnumDiscountText(magnumDiscountInfo.value);
});


const currencyFormatter = (value) => {
  return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(value / 100)
}

const formatPriceForDataLayer = (priceInCents) => {
  return (priceInCents / 100).toFixed(2);
}

const handleRemove = () => {
  props.dentaidApiInstance.post(`/cart/delete?token=${piniaStore.parsedToken}`, {
    items: {
      "0": {
        sku: props.item.sku,
      }
    }
  }).then(response => {
    pushRemoveFromCartToDataLayer();

    piniaStore.updateUserData(response.data.data);
  }).catch(error => {
    Vue.$toast.error('Er is iets fout gegaan!');
  }).finally(() => {
    props.cartEventBus.$emit('refresh-badge')
  });
}

const handleQuantityChanged = (data) => {
  if (!_.isEqual(props.item.sku, data.crmId)) {
    return;
  }

  props.dentaidApiInstance.post(`/cart?token=${piniaStore.parsedToken}`, {
    items: {
      "0": {
        sku: props.item.sku,
        quantity: data.quantity,
      }
    }
  }).then(response => {
    piniaStore.updateUserData(response.data.data);

    setTimeout(() => {
      pushUpdateCartToDataLayer();
    }, 250)
  }).catch(error => {
    Vue.$toast.error('Er is iets fout gegaan!');
  }).finally(() => {
    props.cartEventBus.$emit('refresh-badge')
  });
}

const calculateUnitDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const quantity = product.quantity || 1;
  const discountPerUnit = (totalUnitPrice - totalNetPrice) / quantity;
  return formatPriceForDataLayer(discountPerUnit);
}

const calculateTotalProductDiscount = (product) => {
  const totalUnitPrice = product.total_unit_price || 0;
  const totalNetPrice = product.total_net_price || 0;
  const totalDiscount = totalUnitPrice - totalNetPrice;
  return formatPriceForDataLayer(totalDiscount);
}

const pushUpdateCartToDataLayer = () => {
  const totalProductPrice = formatPriceForDataLayer(props.item.unit_price * props.item.quantity);
  const totalProductDiscount = calculateTotalProductDiscount(props.item);

  window.dataLayer.push({ecommerce: null});
  window.dataLayer.push({
    'event': 'addToCart',
    'ecommerce': {
      'currencyCode': 'EUR',
      'add': {
        'products': [{
          'name': (props.item.dwh_product?.name ?? props.item.translation.name)?.toLowerCase(),
          'id': props.item.sku,
          'brand': props.item.translation.extra_attributes.brand,
          'price': formatPriceForDataLayer(props.item.unit_price),
          'quantity': props.item.quantity,
          'discount': calculateUnitDiscount(props.item),
          'total_product_price': (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
          'total_product_discount': totalProductDiscount,
        }]
      }
    }
  });
}

const pushRemoveFromCartToDataLayer = () => {
  const totalProductPrice = formatPriceForDataLayer(props.item.unit_price * props.item.quantity);
  const totalProductDiscount = calculateTotalProductDiscount(props.item);

  window.dataLayer.push({ecommerce: null});
  window.dataLayer.push({
    'event': 'removeFromCart',
    'ecommerce': {
      'remove': {
        'products': [{
          'name': (props.item.dwh_product?.name ?? props.item.translation.name)?.toLowerCase(),
          'id': props.item.sku,
          'brand': props.item.translation.extra_attributes.brand,
          'price': formatPriceForDataLayer(props.item.unit_price),
          'quantity': props.item.quantity,
          'discount': calculateUnitDiscount(props.item),
          'total_product_price': (parseFloat(totalProductPrice) - parseFloat(totalProductDiscount)).toFixed(2),
          'total_product_discount': totalProductDiscount,
        }]
      }
    }
  });
}

onMounted(() => {
  setTimeout(() => {
    props.eventBus.$on('quantity-changed', (data) => {
      handleQuantityChanged(data)
    });
  }, 1000)
})
</script>

<template>
  <div class="vue-cart-row">
    <div class="row d-flex align-items-center">
      <div class="col-xl-2 col-md-4 col-3">
        <img :src="props.item.translation?.image" :alt="item.name">
      </div>

      <div class="col-xl-5 col-md-8 col-9">
        <h5 class="item-title" v-if="item.translation.url">
          <a :href="item.translation.url" :title="item.translation.name">
            {{ item.translation.name }}
          </a>
        </h5>
        <h5 class="item-title" v-else>{{ item.translation.name }}</h5>

        <span class="meta-info">{{ articleNumberLabel }} {{
            displayedArticleNumber
          }} - {{ item.translation.extra_attributes.volume }} {{ showCnk }}</span>

        <button class="delete-from-cart" @click="handleRemove">{{ deleteProductText }}</button>
      </div>

      <div class="col-xl-5 col-md-12 mt-xl-05 position-relative">
        <div class="row d-flex align-items-center">
          <div class="col-xl-5 col-md-6 mt-5 col-sm-12">
            <dentaid-product-quantity
              :event-bus="eventBus"
              :initial-quantity="item.quantity"
              :crm-id="item.sku"
              :incremental-order-quantity="item.incremental_order_quantity"
              :minimum-order-quantity="item.min_order_quantity"
              :cart-event-bus="cartEventBus"
            ></dentaid-product-quantity>
          </div>

          <div class="col-xl col-md-3 mt-5 col-6">
            {{ currencyFormatter(item.unit_price) }}
          </div>

          <div class="col-xl col-md-3 mt-5 col-6">
            <span class="font-weight-bold">
              {{ currencyFormatter(item.total_unit_price) }}
            </span>
          </div>
        </div>

        <div class="line-discount-wrapper position-absolute text-center" v-if="hasLineDiscount">
          <span class="line-discount text-success" v-if="magnumDiscountText">
            {{ magnumDiscountText }}
          </span>
          <span class="line-discount text-success" v-else>
            {{ discountLabelText }} - {{ currencyFormatter(lineDiscount) }} ({{ lineDiscountPercentage }}{{ isFrench ? ' ' : '' }}%)
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
